import styled, { css, ThemeContext, props } from 'styled-components';
import { useContext } from 'react';
import { rem } from 'polished';
import { Col, Container } from '../../base/Grid'
import { relative } from 'path';

export const ContainerDefault = styled(Container)`
  && { // doubles specificity
    max-width: ${rem('1440px')};
    position: relative;
    margin: 0 auto;
    background: transparent;
  }
`

// TODO: move styles into Component
export const InformationalBanner = styled.div`
  ${({ theme }) => {
    const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));
    return css`
      display: flex;
      max-width: ${rem('2000px')};
      width: 100%;
      min-height: ${rem('50px')};
      margin: 0 auto;
      align-items: center;
      box-shadow: ${theme.shadows.inner};
      z-index: ${theme.zIndex.informationalBanner};
      position: ${mediumUp ? 'relative' : 'fixed'};
    `
  }}
`;

export const DismissButton = styled.button`
  ${({ theme }) => {
    const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));
    return css`
      position: ${mediumUp ? 'relative' : 'absolute'};
      right: ${mediumUp ? rem('24px') : rem('16px')};
      top: ${mediumUp ? 'unset' : 0};
      width: ${mediumUp ? rem('18px') : rem('14px')};
      height: ${mediumUp ? rem('18px') : rem('14px')};
      margin-top: ${mediumUp ? 0 : rem('16px')};
      padding: 0;
      border: 0;
      background: rgba(255, 255, 255, 0.2); /* TODO: use highlight color function */
      cursor: pointer;
      ::before, ::after {
        content: '';
        width: ${rem('2px')};
        height: 100%;
        background-color: ${theme.colors.neutral.grey29};
        transform: rotate(45deg) translateX(0);
        position: absolute;
        left: 50%;
        top: 0;
      }
      ::before {
        transform: rotate(-45deg) translateX(0);
      }
      :hover::before, :hover::after {
        background-color: ${theme.colors.neutral.grey54};
      }
    `
  }}
`

export const DataSeparator = styled.span`
  margin: 0 ${rem('12px')};
`

export const DataTextContainer = styled.p`
  ${({ theme, variant }) => {
    const mediumDown = theme.useMediaQuery(theme.breakpoints.down('medium'));
    const largeDown = theme.useMediaQuery(theme.breakpoints.down('large'));
    return css`
      && {
        display: inline-block;
        width: ${variant ? '100%' : 'auto'};
        font-weight: 300;
        font-size: ${mediumDown ? rem('14px') : rem('16px')};
        line-height: ${mediumDown ? rem('21px') : rem('24px')};
        color: ${variant === 'drawer-contents' ? theme.colors.neutral.grey29 : theme.colors.primary.blue};
        text-align: ${mediumDown
          && (variant !== 'drawer') ? 'left' : 'center'
          && (variant === 'drawer-contents') ? 'left' : 'center'
          && (variant === 'cta-button' && largeDown) ? 'left' : 'center'
        };
        background: transparent;
      }
    `
  }}
`

export const StartAdornment = styled.span`
  ${({ theme, variant }) => {
    const smallDown = theme.useMediaQuery(theme.breakpoints.down('small'));
    const mediumDown = theme.useMediaQuery(theme.breakpoints.down('medium'));
    const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));
    const largeDown = theme.useMediaQuery(theme.breakpoints.down('large'));

    let thisPosition
    if (variant === 'cta-button') {
      thisPosition = largeDown ? 'absolute' : 'relative'
    } else if (variant !== 'drawer') {
      thisPosition = mediumDown ? 'absolute' : 'relative'
    }
    return css`
      position: ${thisPosition};
      left: 0;
      margin-left: ${mediumUp
        && variant === 'text-link' ? rem('0px') : rem('16px')
      };
      margin-right: ${mediumUp
        && (variant !== 'drawer') ? rem('20px') : 0
      };
      display: inline-block;
      .nmx-icon-start-adornment {
        width: ${mediumUp ? rem('20px') : rem('18px')};
        height: ${mediumUp ? rem('20px') : rem('18px')};
      }
      .icon-fill {
        color: ${theme.colors.cta.blue}
      }
    `
  }}
`

// TODO: should be the same as DataTextContainer, so look to combine
export const DrawerContents = styled.p`
  ${({ theme }) => {
    const mediumDown = theme.useMediaQuery(theme.breakpoints.down('medium'));
    return css`
      && {
        display: inline-block;
        width: ${variant ? '100%' : 'auto'};
        font-weight: 300;
        font-size: ${rem('16px')};
        color: ${theme.colors.primary}
        text-align: ${mediumDown && variant !== 'drawer' ? 'left' : 'center'};
      }
    `
  }}
`

// Creates inline styles (to override stock Component Library styles)
export const Styles = () => {
  const theme = useContext(ThemeContext);
  const mediumDown = theme.useMediaQuery(theme.breakpoints.down('medium'));
  const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));
  const largeDown = theme.useMediaQuery(theme.breakpoints.down('large'));
  const largeUp = theme.useMediaQuery(theme.breakpoints.up('large'));
  return {
    Container: {
      maxWidth: rem(1440),
      position: 'relative',
      margin: `${rem('0px')} auto`,
      background: `transparent`,
    },
    ColInner: (startAdornment, hideDismissButton, variant) => {
      return {
        paddingTop: rem(14),
        paddingRight: rem(40), // default active dismiss button 
        paddingBottom: rem(14),
        paddingLeft: rem(16),

        ...(variant === 'text-link'
          && hideDismissButton
          && { paddingRight: rem(16) }
        ),
        ...(variant === 'text-link'
          && startAdornment
          && { paddingLeft: rem(50) }
        ),
        ...(variant === 'text-link'
          && startAdornment
          && mediumUp
          && {
            paddingLeft: rem(40),
            paddingRight: rem(40)
          }
        ),

        ...(variant === 'cta-button'
          && startAdornment
          && { paddingLeft: rem(50) }
        ),
        ...(variant === 'cta-button'
          && mediumDown
          && {
            paddingBottom: rem(16)
          }
        ),
        ...(variant === 'cta-button'
          && mediumUp
          && {
            display: 'flex',
            alignItems: 'center',
          }
        ),

        // reduce right padding from dismiss button
        // ...(!hideDismissButton && { paddingRight: rem(16) }
        // ),
        // right padding for dismiss button
        // ...(!hideDismissButton && {
        //   paddingRight: rem(16)
        // }),

        // left padding for start adornment mobile
        // ...((startAdornment && !mediumUp) && {
        //   paddingLeft: rem(40),
        //   paddingRight: rem(32),
        // }),
        // reset left padding for start adornment desktop (styled components bug?)
        // ...((startAdornment && mediumUp) && {
        //   paddingLeft: rem(14),
        //   paddingRight: rem(24)
        // }),

        // ...((variant === 'cta-button' && !largeUp) && {
        //   paddingLeft: rem(40),
        //   paddingRight: rem(40),
        // }),
        ...((variant === 'drawer') && {
          paddingLeft: 0,
          paddingRight: 0
        })
      }
    },
    ColDrawer: (startAdornment, hideDismissButton) => {
      // console.log('variant', ...colInnerVariant)
      return {
        paddingTop: rem(0),
        paddingRight: rem(16),
        paddingBottom: rem(14),
        paddingLeft: rem(16),
        maxWidth: rem(800),
        margin: `0 auto`,
        // ...(mediumUp && {
        //   padding: rem(14)
        // }),
        // right padding for dismiss button
        ...(!hideDismissButton && {
          paddingRight: rem(20)
        }),
        // left padding for start adornment mobile
        ...((startAdornment && !mediumUp) && {
          paddingLeft: rem(40)
        }),
        // reset left padding for start adornment desktop (styled components bug?)
        ...((startAdornment && mediumUp) && {
          paddingLeft: rem(34)
        }),
      }
    },
    CtaButton: (variant, contentHasStartAdornment) => {
      return {
        // height: rem(30),
        minWidth: rem(180),
        minHeight: rem(32),
        marginLeft: rem(30),
        marginTop: 0,
        padding: `${rem('6px')} ${rem('10px')}`,
        fontSize: rem(16),
        lineHeight: rem(24),
        background: 'transparent',
        // mobile styles
        ...(largeDown && {
          marginLeft: 0,
          marginTop: rem(16),
          minWidth: rem(160), // TODO: revisit if text width changes?
          minHeight: rem(44),
          fontSize: rem(14),
          lineHeight: rem(20),
        }),
        // cta-button
        ...((variant === 'cta-button' && largeDown) && {
          marginRight: rem(8),
        }),
        ...(variant === 'cta-button'
          && mediumDown
          && !contentHasStartAdornment
          && {
            marginRight: 0,
            marginLeft: rem(24)
        }),
        ...(variant === 'cta-button'
          && mediumUp
          && largeDown
          && {
            marginTop: 0,
            marginRight: rem(32),
            marginLeft: rem(24)
        }),
        ...(variant === 'cta-button'
          && largeUp
          && {
            // minWidth: rem(120),
            marginTop: 0,
            marginRight: rem(32),
            marginLeft: rem(24)
        }),
        // drawer
        ...(variant === 'quarternary' && {
          marginLeft: 0,
          marginTop: 0,
          border: 'none'
          && mediumDown
          && {
            minHeight: rem(42),
          }
        }),
        // drawer mobile
        ...((variant === 'quarternary' && !largeUp) && {
          fontSize: rem(14),
          lineHeight: rem(16),
        }),
      }
    }
  };
};
